import { useEffect, useState } from "react";
import axios from "axios";
import { ImageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lightweight progress strip for the SRD portrait batch job.
 *
 * Hides itself when the batch is 100% complete. Polls every 25s while open so
 * users can watch progress fill in without refreshing the Monster Manual tab.
 */
export const SrdPortraitProgress = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let timer;
    let cancelled = false;

    const tick = async () => {
      try {
        const res = await axios.get(`${API}/admin/srd-portraits/status`, { withCredentials: true });
        if (!cancelled) setStatus(res.data);
      } catch { /* silent — endpoint isn't critical */ }
    };

    tick();
    timer = setInterval(tick, 25000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  if (!status) return null;
  if (status.progress_pct >= 100) return null;

  const pct = Math.max(0, Math.min(100, status.progress_pct || 0));
  return (
    <div
      className="mb-4 px-4 py-3 rounded-sm border border-primary/20 bg-primary/5 flex items-center gap-3"
      data-testid="srd-portrait-progress"
    >
      <ImageIcon className="w-4 h-4 text-primary flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-primary">
          Painting the Bestiary &middot; {status.done}/{status.total} portraits ready ({pct.toFixed(1)}%)
        </p>
        <p className="text-[10px] text-muted-foreground mt-0.5">
          Our AI is generating a unique token portrait for every SRD creature. Already-painted ones show on
          their cards and VTT tokens. The rest will appear automatically as they finish.
        </p>
      </div>
      <div className="w-32 hidden sm:block">
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
};

export default SrdPortraitProgress;
